import { captureEvent, Severity } from '@sentry/browser';
import i18n from 'src/config/i18n';
import {
    DataDefinedScreenNode,
    Maybe,
    ScreenDesignatedApplication,
    ScreenNode,
} from 'src/generated/webapp_gql';
import {
    contentLinkNodeToClickHandler,
    contentLinkNodeToPath,
} from 'src/modules/dashboard/helpers/contentLinkNodeNavigateTo';
import { SectionHeaderProps } from '@modernhealth/vital-components';

import { ContentLinkFragment } from '../documents/getHomeScreen.generated';
import { getTrackClickCallback, ContentTracking } from './getTrackClickCallback';

export const screenLinkToPath = (
    node: Pick<ScreenNode, '__typename'>,
    pathname: string = '',
): string | null => {
    if (!node.__typename) return null;

    switch (node.__typename) {
        case 'ForYouScreenNode':
            return '/dashboard?action=foryou';
        case 'HomeScreenNode':
            return '/dashboard';
        case 'MeditationsScreenNode':
            return '/meditations';
        case 'ProgramsScreenNode':
            return '/programs';
        case 'CoursesScreenNode':
            return '/courses';
        case 'ResourcesScreenNode':
            return '/resources';
        case 'RecommendedScreenNode':
            return '/recommended';
        case 'ContentTagListScreenNode':
            return '/topics';
        case 'ContentTagScreenNode':
            captureEvent({
                message: 'You should not be able to get this value at this moment',
            });
            return '/topics';
        case 'WellbeingReportScreenNode':
            captureEvent({
                message: 'Wellbeing Report screen links are not configured on member web.',
            });
            return '/dashboard';
        case 'ManageCareTeamScreenNode':
            return '/care-team';
        case 'DataDefinedScreenNode':
            const dataNode = node as DataDefinedScreenNode;
            if (dataNode.designatedApplication === ScreenDesignatedApplication.HrAdmin) {
                return pathname.concat(`/${dataNode.slug}`);
            } else {
                const _ = node.__typename;
                captureEvent({
                    message: 'Attempted to link to unknown screen node type.',
                    level: Severity.Error,
                    extra: { node: _ },
                });
                return null;
            }

        default:
            const _ = node.__typename;

            captureEvent({
                message: 'Attempted to link to unknown screen node type.',
                level: Severity.Error,
                extra: { node: _ },
            });
    }

    return null;
};

type MapContentLinkToSectionHeaderProps = {
    link?: Maybe<ContentLinkFragment>;
    secondaryText?: string | null;
    tracking: ContentTracking;
    pathname?: string;
};

export const mapContentLinkToSectionHeaderProps = ({
    link,
    secondaryText,
    tracking,
    pathname,
}: MapContentLinkToSectionHeaderProps): Pick<
    SectionHeaderProps,
    'secondaryText' | 'onSecondaryTextPress' | 'secondaryTextLink' | 'trackClickCallback'
> => {
    if (!link) {
        return {
            secondaryText: undefined,
            onSecondaryTextPress: undefined,
        };
    }

    const secondaryTextToDisplay = !!secondaryText ? secondaryText : i18n.t('shared:view_all');

    switch (link.__typename) {
        case 'InternalLinkNode':
            return {
                secondaryText: secondaryTextToDisplay,
                onSecondaryTextPress: contentLinkNodeToClickHandler(link) ?? undefined,
                secondaryTextLink: contentLinkNodeToPath(link) ?? undefined,
                trackClickCallback: getTrackClickCallback({
                    link,
                    ...tracking,
                    props: { destination: link.path, ...tracking.props },
                }),
            };
        case 'ScreenLinkNode':
            const path = screenLinkToPath(link.screen, pathname);
            if (path) {
                return {
                    secondaryText: secondaryTextToDisplay,
                    secondaryTextLink: path,
                    trackClickCallback: getTrackClickCallback({
                        link,
                        ...tracking,
                        props: { destination: path, ...tracking.props },
                    }),
                };
            }
    }

    return {
        secondaryText: undefined,
        onSecondaryTextPress: undefined,
    };
};
